<template>
  <div v-if="requestLetter.id">
    <div class="row">
      <div class="col-md-10">
        <b-card no-body>
          <div class="m-2">
            <div class="row">
              <div class="col-md-6">
                <table class="table table-bordered">
                  <tr>
                    <th>{{ $t('Title') }}</th>
                    <td>{{ requestLetter.title }}</td>
                  </tr>
                  <tr>
                    <th>{{ $t('Organization') }}</th>
                    <td>{{ $langName(requestLetter.org && requestLetter.org.name) }}</td>
                  </tr>
                  <tr>
                    <th>{{ $t('Created At') }}</th>
                    <td>{{ formatDate(requestLetter.created_at) }}</td>
                  </tr>
                  <tr>
                    <th>{{ $t('Updated At') }}</th>
                    <td>{{ formatDate(requestLetter.updated_at) }}</td>
                  </tr>
                </table>
              </div>
              <div class="col-md-6">
                <table class="table table-bordered">
                  <tr>
                    <th>{{ $t('Status') }}</th>
                    <td>
                      <b-badge :variant="getStatus(requestLetter.status).variant">
                        {{ $langName(getStatus(requestLetter.status).name) }}
                      </b-badge>
                    </td>
                  </tr>
                  <tr>
                    <th>{{ $t('Comment') }}</th>
                    <td>{{ requestLetter.comment || '-' }}</td>
                  </tr>
                  <tr>
                    <th>{{ $t('Body') }}</th>
                    <td class="white-space-pre-line">
                      {{ requestLetter.body }}
                    </td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
        </b-card>
      </div>
      <div class="col-md-2">
        <b-card no-body>
          <div class="m-2">
            <b-button
              v-if="canSend(requestLetter)"
              variant="primary"
              class="btn btn-primary btn-block collapsed"
              @click="send"
            >
              <feather-icon
                icon="SendIcon"
                class="mr-50"
              />
              <span>{{ $t('Send') }}</span>
            </b-button>
            <b-button
              v-if="canReview(requestLetter)"
              v-b-toggle.sidebar-review-request-letter
              variant="success"
              class="btn btn-success btn-block collapsed"
            >
              <feather-icon
                icon="CheckCircleIcon"
                class="mr-50"
              />
              <span>{{ $t('Reject or accept') }}</span>
            </b-button>
            <b-link
              v-if="requestLetter.attachment_file"
              :href="requestLetter.attachment_file"
              class="btn btn-warning btn-block collapsed"
              download="file"
              target="_blank"
            >
              <feather-icon
                icon="DownloadIcon"
                class="mr-50"
              />
              <span>{{ $t('Download') }}</span>
            </b-link>
          </div>
        </b-card>
      </div>
    </div>

    <b-card
      v-if="applyDocs.length"
      no-body
    >
      <b-table
        responsive
        :items="applyDocs"
        :fields="applyDocFields"
        class="mb-0"
      >
        <template #cell(index)="data">
          {{ data.index + 1 }}
        </template>
        <template #cell(dc_type_name)="data">
          {{ $langName(data.value) }}
        </template>
        <template #cell(direction_name)="data">
          {{ $langName(data.value) }}
        </template>
        <template #cell(actions)="data">
          <feather-icon
            v-if="canEditApplyDocs"
            v-b-toggle.sidebar-request-letter-apply-doc-edit
            icon="EditIcon"
            class="cursor-pointer"
            size="16"
            @click="editApplyDoc(data.item)"
          />
        </template>
      </b-table>
    </b-card>

    <review
      :request-letter="requestLetter"
      @refetchData="refetchData"
    />
    <apply-doc-edit
      :request-letter-id="requestLetter.id"
      @refetchData="refetchData"
    />
  </div>
</template>

<script>
import {
  BBadge, BButton, BCard, BLink, BTable, VBToggle,
} from 'bootstrap-vue'
import { onUnmounted } from '@vue/composition-api'
import store from '@/store'
import requestLettersModule from '@/store/request-letters'
import applyDocumentModule from '@/store/apply-document'
import { isAdmin } from '@/auth/utils'
import Review from './Review.vue'
import ApplyDocEdit from './ApplyDocEdit.vue'
import { REQUEST_LETTER_STATUSES, getRequestLetterStatus } from './config'
import permission from './permission'

export default {
  directives: {
    'b-toggle': VBToggle,
  },
  components: {
    BBadge,
    BButton,
    BCard,
    BLink,
    BTable,
    Review,
    ApplyDocEdit,
  },
  computed: {
    requestLetter() {
      return this.$store.state[this.MODULE_NAME].requestLetter
    },
    canEditApplyDocs() {
      return isAdmin() && this.requestLetter.status === REQUEST_LETTER_STATUSES.PENDING
    },
    applyDocs() {
      if (Array.isArray(this.requestLetter.apply_docs)) return this.requestLetter.apply_docs
      if (this.requestLetter.apply_docs && typeof this.requestLetter.apply_docs === 'object') {
        return Object.values(this.requestLetter.apply_docs)
      }
      return []
    },
    applyDocFields() {
      const fields = [
        { key: 'index', label: '#' },
        { key: 'fio', label: this.$t('Full name') },
        { key: 'dc_type_name', label: this.$t('Dc Type') },
        { key: 'status_name', label: this.$t('Status') },
        { key: 'direction_name', label: this.$t('Direction') },
      ]
      if (this.canEditApplyDocs) {
        fields.push({ key: 'actions', label: this.$t('Actions') })
      }
      return fields
    },
  },
  created() {
    this.refetchData()
  },
  methods: {
    getStatus(status) {
      return getRequestLetterStatus(status)
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString()
    },
    refetchData() {
      this.$store.dispatch(`${this.MODULE_NAME}/get`, this.$route.params.id)
    },
    editApplyDoc(applyDoc) {
      this.$store.commit('applyDocument/UPDATE', {
        id: applyDoc.id,
        fio: applyDoc.fio,
        dc_type: applyDoc.dc_type,
        edu_lang: applyDoc.edu_lang,
        organization: applyDoc.organization_id,
        organization_name: applyDoc.organization_name,
        direction_id: applyDoc.direction_id,
        direction_name: applyDoc.direction_name,
        status: Array.isArray(applyDoc.apply_status)
          ? applyDoc.status || null
          : applyDoc.apply_status || applyDoc.status || null,
        status_name: applyDoc.status_name,
        apply_status: applyDoc.apply_status || [],
      })
      if (applyDoc.organization_id) {
        this.$store.dispatch('applyDocument/directions', { org: applyDoc.organization_id, limit: 9999 })
      }
    },
    send() {
      this.$removeSwal(this, this.requestLetter.id, `${this.MODULE_NAME}/send`, this.refetchData, {
        text: '',
        confirmButtonText: 'Send',
        successTitle: 'Sent',
        successText: 'Request sent successfully',
        errorText: 'Failed to send request',
      })
    },
  },
  setup() {
    const MODULE_NAME = 'requestLetters'
    const APPLY_DOCUMENT_MODULE_NAME = 'applyDocument'
    if (!store.hasModule(MODULE_NAME)) store.registerModule(MODULE_NAME, requestLettersModule)
    if (!store.hasModule(APPLY_DOCUMENT_MODULE_NAME)) store.registerModule(APPLY_DOCUMENT_MODULE_NAME, applyDocumentModule)

    onUnmounted(() => {
      if (store.hasModule(MODULE_NAME)) store.unregisterModule(MODULE_NAME)
      if (store.hasModule(APPLY_DOCUMENT_MODULE_NAME)) store.unregisterModule(APPLY_DOCUMENT_MODULE_NAME)
    })

    return {
      MODULE_NAME,
      ...permission(),
    }
  },
}
</script>

<style scoped>
.white-space-pre-line {
  white-space: pre-line;
}
</style>
